import type { BillingPlanUpgradeSource } from "@editor/reducers/api-reducer";
import type { ButtonVariant } from "@replo/design-system/components/button/button-shared";
import type { PlanInfo } from "schemas/billing";
import type {
  BillingInterval,
  BillingTier,
  SubscriptionDetails,
} from "schemas/generated/billing";
import type { Workspace } from "schemas/generated/workspace";

import * as React from "react";

import { useSubscriptionDetails } from "@editor/hooks/subscription";
import useInitializeSubscriptionUpdateMutation from "@editor/hooks/trpc/useInitializeSubscriptionUpdateMutation";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { useModal } from "@editor/hooks/useModal";

import Button from "@replo/design-system/components/button/Button";
import { Tag } from "@replo/design-system/components/tag/Tag";
import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { Check } from "lucide-react";
import { useParams } from "react-router-dom";

import { getPlanColorTheme, getPlanIcon } from "./planUtils";

type PlanCardProps = {
  plan: PlanInfo;
  currentPlan: BillingTier;
  isYearly: boolean;
  isWorkspaceOwner: boolean;
  workspace?: Workspace;
  source: BillingPlanUpgradeSource;
};

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  currentPlan,
  isYearly,
  isWorkspaceOwner,
  workspace,
  source,
}) => {
  const modal = useModal();
  const params = useParams();
  const analytics = useLogAnalytics();
  const { user } = useCurrentUser();
  const workspaceId = workspace?.id;
  const { data: subscriptionDetails } = useSubscriptionDetails(workspaceId);
  const targetPlan = plan.tier;
  const colors = getPlanColorTheme(targetPlan);
  const isCurrentTier = currentPlan === targetPlan;
  const isCurrentlyTierMonthly =
    subscriptionDetails?.billingInterval === "monthly";
  const isYearlyUpgrade = isCurrentTier && isCurrentlyTierMonthly && isYearly;

  const getPlanRank = (tier: string) => {
    const planRanks = { free: 0, basic: 1, standard: 2, growth: 3, custom: 4 };
    return planRanks[tier as keyof typeof planRanks] || 0;
  };

  const isDowngrade =
    !isCurrentTier && getPlanRank(targetPlan) < getPlanRank(currentPlan);

  const isCustomPlan = targetPlan === "custom";

  let buttonText: string;
  let buttonVariant: ButtonVariant;

  if (isYearlyUpgrade) {
    buttonText = "Upgrade to Annual";
    buttonVariant = "primary";
  } else if (isCurrentTier) {
    buttonText = "Current Plan";
    buttonVariant = "secondary";
  } else if (isCustomPlan) {
    buttonText = "Let's Talk →";
    buttonVariant = "secondary";
  } else if (isDowngrade) {
    buttonText = "Downgrade";
    buttonVariant = "secondary";
  } else {
    buttonText = "Upgrade";
    buttonVariant = "primary";
  }

  const billingInterval = isYearly ? "yearly" : "monthly";

  const isActiveSubscriptionMarkedAsCustom =
    subscriptionDetails?.isCustom ?? false;

  const { initializeSubscriptionUpdate } =
    useInitializeSubscriptionUpdateMutation();

  const initializeSubscriptionChange = (
    tier: BillingTier,
    billingInterval: BillingInterval,
    subscriptionDetails?: SubscriptionDetails,
  ) => {
    const makeTierChange = () => {
      if (!workspace) {
        return;
      }

      const location = window.location;
      let pathname = location.pathname;

      // Note (Fran, 2024-08-08): If we're coming from the editor page, we want to
      // redirect to the project page, not the editor page. This is because Shopify has a
      // limit of 255 characters for return_url length, and passing the elementId param can hit
      // that limit
      if (
        params.projectId &&
        params.elementId &&
        (subscriptionDetails?.paymentProcessor === "shopify" ||
          (!subscriptionDetails?.paymentProcessor &&
            workspace.preferredPaymentProcessor === "shopify"))
      ) {
        pathname = `/editor/${params.projectId}`;
      }
      // Note (Noah, 2022-11-02, REPL-4890): We used to try to pass query params
      // along to this nextUrl, but the problem is that Shopify has a limit of 255
      // characters for return_url length, and passing params can hit that limit
      const nextUrl = `${location.origin}${pathname}`;
      void initializeSubscriptionUpdate({
        workspaceId: workspace.id,
        tier,
        billingInterval,
        nextUrl,
        source,
      }).then(() => {
        if (workspace.referralCode?.code) {
          analytics("referralCode.redeemed", {
            workspaceId: workspace.id,
            code: workspace.referralCode.code,
            referralCodeId: workspace.referralCode.id,
            email: user?.email ?? "",
          });
        }
      });
    };

    if (
      subscriptionDetails?.paymentProcessor === "shopify" &&
      workspace?.preferredPaymentProcessor === "stripe"
    ) {
      modal.closeModal({ type: "billingModal" });
      modal.openModal({
        type: "billingStripeNotificationModal",
        props: {
          onConfirm: makeTierChange,
          buttonText: "Continue",
        },
      });
    } else {
      makeTierChange();
    }
  };

  // Note (Evan, 2024-03-26): There are 3 cases to account for here:
  // 1) If there's an active Stripe subscription, redirect to the billing plan confirmation modal
  // 2) If there's no active Stripe subscription and we're downgrading, redirect to the survey modal
  // 3) If there's no active Stripe subsbscription and we're upgrading, initiate the update immediately
  // (this is because Shopify (for upgrades) and Stripe (for new subscriptions) require confirmation already)
  const handleSubmit = () => {
    if (!workspace) {
      return;
    }

    if (isCustomPlan) {
      window.open("https://replo.app/contact", "_blank");
      return;
    }

    if (currentPlan === "custom" && isDowngrade) {
      modal.openModal({
        type: "billingModal",
        props: { source: "direct.billingDashboard" },
      });
      return;
    }

    const isCurrentInterval =
      subscriptionDetails?.billingInterval === billingInterval;

    if (isCurrentTier && isCurrentInterval) {
      return;
    }

    const isUncancelledStripeSubscription =
      subscriptionDetails?.paymentProcessor === "stripe" &&
      !subscriptionDetails?.endedAt;

    if (!isUncancelledStripeSubscription) {
      if (isDowngrade) {
        modal.closeModal({ type: "billingModal" });
        modal.openModal({
          type: "billingPlanChangeSurvey",
          props: {
            tier: currentPlan,
            downgradeTo: targetPlan,
            newBillingInterval: billingInterval,
            source,
            type: "downgrade",
            workspaceId: workspace.id,
          },
        });
      } else {
        initializeSubscriptionChange(
          targetPlan,
          billingInterval,
          subscriptionDetails ?? undefined,
        );
      }
      return;
    }

    modal.closeModal({ type: "billingModal" });
    modal.openModal({
      type: "billingUpgradeDowngradeModal",
      props: {
        workspaceId: workspace.id,
        currentTier: currentPlan,
        newTier: targetPlan,
        type: isDowngrade ? "downgrade" : "upgrade",
        source,
        newBillingInterval: billingInterval,
      },
    });
  };

  const isButtonDisabled =
    (isCurrentTier && !isYearlyUpgrade) ||
    (!isWorkspaceOwner && !isCustomPlan) ||
    (isActiveSubscriptionMarkedAsCustom && !isCustomPlan);

  const isPopularPlan = currentPlan === "free" && targetPlan === "standard";

  return (
    <div
      className={twMerge(
        "rounded-lg border",
        "p-5 flex flex-col min-h-[458px] h-full overflow-hidden relative",
        // eslint-disable-next-line replo/no-tailwind-literal-color
        isPopularPlan ? `border-indigo-600 border-3` : colors.cardBorder,
        colors.cardBg,
      )}
    >
      {isPopularPlan && (
        <Tag
          size="base"
          variant="neutral"
          layoutClassName="absolute top-7 right-3"
          UNSAFE_className="bg-white"
        >
          Most Popular 🎉
        </Tag>
      )}
      <div className="flex flex-col items-start mb-4">
        <div className="mb-3 mt-2 flex items-center gap-2">
          {getPlanIcon(
            targetPlan,
            colors.stroke,
            `flex items-center justify-center w-10 h-10 rounded-full border border-secondary bg-white shadow-sm`,
          )}
        </div>
        <h3 className="text-lg font-semibold">{plan.displayName}</h3>
      </div>

      <p className="text-base text-muted mb-4 overflow-hidden text-ellipsis line-clamp-3">
        {plan.description}
      </p>

      {targetPlan !== "custom" ? (
        <>
          <div className="text-2xl font-bold mb-1 flex flex-wrap items-baseline">
            <span>
              ${isYearly ? plan.yearlyPricePerMonth : plan.monthlyPrice}
            </span>
            <span className="text-base font-normal ml-1">/month</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-sm text-muted">
              Billed {isYearly ? "annually" : "monthly"}
            </span>
            {isYearly && (
              <span
                className={twMerge(
                  colors.bg,
                  colors.text,
                  "text-xs px-1.5 py-0.5 rounded whitespace-nowrap",
                )}
              >
                Save 17%
              </span>
            )}
          </div>
        </>
      ) : (
        <div className="text-2xl font-bold mb-6">Need more?</div>
      )}

      <ul className="flex-1 space-y-2 mb-6 overflow-y-auto">
        {plan.features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2 text-sm">
            <Check
              size={16}
              className={twMerge(colors.stroke, "shrink-0 mt-0.5")}
              strokeWidth={2}
            />
            <span className="overflow-hidden text-ellipsis">{feature}</span>
          </li>
        ))}
      </ul>

      {isActiveSubscriptionMarkedAsCustom && !isCustomPlan ? (
        <Tooltip
          content="Your workspace has a custom subscription. Please contact support to make changes."
          triggerAsChild
        >
          <Button
            variant={buttonVariant}
            size="lg"
            disabled={isButtonDisabled}
            layoutClassName="w-full mt-auto"
            onClick={handleSubmit}
          >
            {buttonText}
          </Button>
        </Tooltip>
      ) : (
        <Button
          variant={buttonVariant}
          size="lg"
          disabled={isButtonDisabled}
          layoutClassName="w-full mt-auto"
          onClick={handleSubmit}
        >
          {buttonText}
        </Button>
      )}
    </div>
  );
};

export default PlanCard;
